/** The React seam: one engine per app, and the two taps that feed it.
 *
 * `AudioProvider` is the only thing that constructs an `AudioEngine`, and it is
 * the only thing that knows a browser gesture is what opens a context. Screens
 * ask for `useSound()` and get a function; they never see the engine, the
 * context or the bus.
 */

import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useMemo, useRef } from "react";
import type { GameEvent } from "@game/core";

import type { Cue } from "./cues.js";
import type { AudioVolumes } from "./engine.js";
import { AudioEngine, DEFAULT_VOLUMES } from "./engine.js";
import type { Playlist, Scene, Track } from "./music.js";
import { toPlaylist } from "./music.js";

const AudioContextValue = createContext<AudioEngine | null>(null);

export interface AudioProviderProps {
  readonly volumes?: AudioVolumes;
  readonly children: ReactNode;
}

/**
 * The gesture listeners are on `window` in the capture phase and never removed
 * until unmount, because "the first gesture" is not a single event: a browser
 * may suspend the context again when the tab loses focus, and the next click is
 * what has to bring it back.
 */
export function AudioProvider(props: AudioProviderProps): JSX.Element {
  const volumes = props.volumes ?? DEFAULT_VOLUMES;
  const engine = useMemo(() => new AudioEngine(volumes), []);

  useEffect(() => {
    const unlock = (): void => { engine.unlock(); };
    window.addEventListener("pointerdown", unlock, { capture: true });
    window.addEventListener("keydown", unlock, { capture: true });
    return () => {
      window.removeEventListener("pointerdown", unlock, { capture: true });
      window.removeEventListener("keydown", unlock, { capture: true });
      engine.close();
    };
  }, [engine]);

  useEffect(() => { engine.setVolumes(volumes); },
    [engine, volumes.master, volumes.sfx, volumes.music]);

  return (
    <AudioContextValue.Provider value={engine}>{props.children}</AudioContextValue.Provider>
  );
}

/** The engine, or null outside a provider - which a screenshot harness is. */
export function useAudioEngine(): AudioEngine | null {
  return useContext(AudioContextValue);
}

/**
 * A stable `play` for a screen to hang on a button.
 *
 * Stable is the point: this ends up in `onClick` on several hundred elements,
 * and a new identity per render would defeat every `memo` in the kit.
 */
export function useSound(): (cue: Cue) => void {
  const engine = useAudioEngine();
  const ref = useRef(engine);
  ref.current = engine;
  return useMemo(() => (cue: Cue) => { ref.current?.play(cue); }, []);
}

/**
 * Tell the engine which piece belongs to the screen that is mounted, and the
 * recording for it if the host has one - or several, which the engine plays
 * in rotation. Keyed on the tracks' fields rather than their identity, so a
 * host that builds the list per render does not restart the music per render.
 */
export function useMusicScene(scene: Scene | null, tracks: Track | Playlist | null = null): void {
  const engine = useAudioEngine();
  const playlist = toPlaylist(tracks);
  const key = playlist.map((t) => `${t.url}@${String(t.loopSeconds)}`).join("\n");
  const latest = useRef(playlist);
  latest.current = playlist;
  useEffect(() => {
    engine?.setScene(scene, latest.current.map((t) => ({ url: t.url, loopSeconds: t.loopSeconds })));
  }, [engine, scene, key]);
}

/**
 * Sound the kernel's events.
 *
 * The caller passes a cursor into a *growing* log rather than a drained queue,
 * because the renderer already drains the queue and two consumers of one queue
 * is a race whose loser goes silent. `revision` is what tells this hook the log
 * grew: the session pushes into the same array, so its identity never changes
 * and an effect keyed on the array alone would fire exactly once.
 *
 * The cursor is a ref so that a re-render never re-plays a batch, and it is
 * clamped when the log shrinks - which is what loading a save looks like from
 * here, and it must be silent rather than replaying the whole game at once.
 */
export function useEventSound(
  log: readonly GameEvent[], revision: number, enabled = true,
  /**
   * Thin the batch before it is sounded - the host uses it to drop the events
   * that belong to another kingdom, so the player does not hear the computer's
   * buildings going up in a town he has never seen.
   *
   * Applied to the batch AFTER the cursor has sliced it, never to the log
   * itself: the cursor is an index into the unfiltered log, and a selector
   * that reads live ownership can change its mind about an old event when a
   * town changes hands. Filtering the log would shift that index under the
   * cursor and replay - or swallow - a batch of sounds.
   */
  select?: (batch: readonly GameEvent[]) => readonly GameEvent[],
): void {
  const engine = useAudioEngine();
  const cursor = useRef(0);

  useEffect(() => {
    if (cursor.current > log.length) cursor.current = log.length;
    if (!enabled || engine === null) { cursor.current = log.length; return; }
    if (cursor.current === log.length) return;
    const batch = log.slice(cursor.current);
    cursor.current = log.length;
    const heard = select === undefined ? batch : select(batch);
    if (heard.length === 0) return;
    engine.playEvents(heard, typeof performance === "undefined" ? 0 : performance.now());
  }, [engine, log, revision, enabled, select]);
}

/**
 * Give every button in the app a click, from one listener.
 *
 * The alternative is a `useSound()` call on several hundred `onClick`s across
 * fourteen screens, every one of which is a place to forget one. A delegated
 * listener in the capture phase hears them all and hears nothing else: a drag
 * across the adventure map is not a button, and neither is a hex.
 */
export function useButtonClicks(enabled = true): void {
  const engine = useAudioEngine();
  useEffect(() => {
    if (!enabled || engine === null) return;
    const onDown = (e: PointerEvent): void => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      const el = t.closest("button, [role=\"button\"]");
      if (el === null) return;
      engine.play(el.hasAttribute("disabled") ? "ui.refused" : "ui.click");
    };
    window.addEventListener("pointerdown", onDown, { capture: true });
    return () => { window.removeEventListener("pointerdown", onDown, { capture: true }); };
  }, [engine, enabled]);
}
